import { StatementPismo } from '@portal/models/bank';
import { TransactionFlow } from '@portal/models/enumerators/index';
import { getInstance } from '@portal/repositories/securitySdkConfig';

const BASE_URL = '/transaction';
const BASE_URL_V2 = '/v2/transaction';
const BASE_URL_V3 = '/v3/transaction';

export type TransactionScheduledResponse = {
  id: string;
  createdBy: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string;
  deletedBy: string | null;
  deletedAt: string | null;
  tenantId: string;
  amount: number;
  type: number;
  status: number;
  accountId: string;
  paymentAccountId: string;
  description: string | null;
  favoredId: string;
  billetKey: string | null;
  dueDate: string;
  transactionScheduleAt: string;
  account: {
    id: string;
    createdBy: string | null;
    createdAt: string;
    updatedBy: string | null;
    updatedAt: string;
    deletedBy: string | null;
    deletedAt: string | null;
    tenantId: string;
    name: string;
    minAccountRequestApprovals: number;
    taxIdentifier: string;
    taxDomicileOutside: string | null;
    accountProfile: number;
    email: string;
    cellphone: string;
    status: number;
    gender: string | null;
    type: number;
    photoUrl: string;
    bornAt: string;
    motherName: string | null;
    fantasyName: string;
    businessRegistration: string | null;
    companyType: number;
    groupType: string | null;
    cnae: string;
    businessLine: number;
    establishmentForm: number;
    financialStatistic: unknown | null;
    onboardSteps: unknown | null;
    bankAccountId: string | null;
    income: number;
    identityCardIssueDate: string | null;
    creationAttempts: number;
    isPep: boolean | null;
    nationality: string | null;
    rated: boolean | null;
    termsOfUse: boolean | null;
    administratorId: string | null;
    convention: boolean;
    pendingConfirmData: string | null;
    externalIdCode: string | null;
    acceptanceOfTariffs: boolean | null;
    approvedAt: string | null;
    capitalSocialValue: number | null;
    annualRevenueValue: number | null;
    companyTypeCategory: string | null;
    businessSegment: string | null;
    isBeneficiary: boolean;
    documentValue: string | null;
    documentType: string | null;
    lastSms: string;
    user: {
      id: string;
      createdBy: string | null;
      createdAt: string;
      updatedBy: string | null;
      updatedAt: string;
      deletedBy: string | null;
      deletedAt: string | null;
      tenantId: string;
      login: string;
      name: string;
      email: string;
      cellphone: string;
      accountId: string;
      status: number;
      mustChangePassword: boolean;
      mustChangePin: boolean;
      language: string | null;
      taxId: string | null;
      lastLogin: string | null;
      lastRecoveryAttempt: string;
      bornAt: string | null;
      recoveryAttempts: number;
      operatorStatus: string | null;
      coreBankId: string | null;
      coreBankAccountRelationId: string | null;
      roleGroupId: string | null;
    };
    proposalId: string | null;
    pixAccountRetries: number | null;
    billetAccountRetries: number | null;
  };
  paymentAccount: {
    id: string;
    createdBy: string | null;
    createdAt: string;
    updatedBy: string | null;
    updatedAt: string;
    deletedBy: string | null;
    deletedAt: string | null;
    tenantId: string;
    type: number;
    coreBankId: string;
    coreBankHolderId: string | null;
    accountId: string;
    bankBranchAccount: string;
    bankAccount: string;
    bankNumber: string | null;
    paymentAccountName: string | null;
    requiredApprovals: number | null;
    status: number;
    investmentProfile: string | null;
    suitabilityExpiration: string | null;
    bankClearingCode: string | null;
    coreBankPersonId: string | null;
  };
  favored: {
    id: string;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
    deletedBy: string | null;
    deletedAt: string | null;
    tenantId: string;
    userId: string;
    accountId: string;
    type: number;
    accountFavoredId: string | null;
    alias: string;
    taxIdentifier: string;
    bankUserName: string;
    bankCode: string | null;
    bankAgency: string;
    bankAccount: string;
    pixKey: string | null;
    bankAccountDigit: string;
    isFavorite: boolean;
    accountType: number;
    personType: string | null;
    paymentAccountId: string | null;
    psp: string;
  };
};

interface GetTransactionsResponse {
  rows: bank.Transaction[];
  isLast: boolean;
}

const TransactionApi = {
  getScheduledTransaction: async (
    id: string
  ): Promise<TransactionScheduledResponse> => {
    const instance = getInstance();
    const { data } = await instance.get<TransactionScheduledResponse>(
      `${BASE_URL_V3}/scheduled/${id}`
    );
    return data;
  },

  getTransactions: async (
    params: advancedFilterModels.TransactionAdvancedFilter
  ) => {
    const instance = getInstance();
    const { data } = await instance.get<GetTransactionsResponse>(
      `${BASE_URL_V2}`,
      { params }
    );

    if (data.rows && Array.isArray(data.rows)) {
      data.rows.sort((a, b) => {
        const bankIdA = Number(String(a.bankId).slice(1)) || 0;
        const bankIdB = Number(String(b.bankId).slice(1)) || 0;
        return bankIdB - bankIdA;
      });
    }

    return data;
  },

  getTransactionsDaysIn: async (
    params: advancedFilterModels.TransactionAdvancedFilter
  ): Promise<StatementPismo> => {
    const instance = getInstance();
    const { data } = await instance.get(`${BASE_URL_V3}/statement-days-in`, {
      params,
    });

    if (data.rows && Array.isArray(data.rows)) {
      data.rows.forEach((day: bank.StatementDay) => {
        if (day.extract && Array.isArray(day.extract)) {
          day.extract.sort(
            (a: bank.StatementItemPismo, b: bank.StatementItemPismo) => {
              const bankIdA =
                Number(
                  String((a as unknown as { bankId?: string }).bankId).slice(1)
                ) || 0;
              const bankIdB =
                Number(
                  String((b as unknown as { bankId?: string }).bankId).slice(1)
                ) || 0;
              return bankIdB - bankIdA;
            }
          );
        }
      });
    }

    if (
      data.scheduledTransactions &&
      Array.isArray(data.scheduledTransactions)
    ) {
      data.scheduledTransactions.sort(
        (a: bank.StatementItemPismo, b: bank.StatementItemPismo) => {
          const bankIdA =
            Number(
              String((a as unknown as { bankId?: string }).bankId).slice(1)
            ) || 0;
          const bankIdB =
            Number(
              String((b as unknown as { bankId?: string }).bankId).slice(1)
            ) || 0;
          return bankIdB - bankIdA;
        }
      );
    }

    return data;
  },

  getPixReversalReasons: async () => {
    const instance = getInstance();
    const { data } = await instance.get(
      `${BASE_URL}/pix-transfer-return-reasons`
    );

    return data;
  },

  getDetails: async (
    id: string,
    params?: { headers: { 'payment-account-id': string } }
  ) => {
    const instance = getInstance();
    const { data } = await instance.get(`${BASE_URL}/${id}`, params);

    return data as bank.Transaction;
  },

  getBilletDetails: async (id: string) => {
    const instance = getInstance();
    const { data } = await instance.get(`${BASE_URL}/billet`, {
      params: { document: id },
    });
    return data;
  },

  getMoneyCashback: async (params: advancedFilterModels.CashbackFilter) => {
    const instance = getInstance();
    const { data } = await instance.get(`${BASE_URL}/cashback`, { params });

    if (data.rows && Array.isArray(data.rows)) {
      data.rows.sort((a: bank.Transaction, b: bank.Transaction) => {
        const bankIdA = Number(String(a.bankId).slice(1)) || 0;
        const bankIdB = Number(String(b.bankId).slice(1)) || 0;
        return bankIdB - bankIdA;
      });
    }

    return data;
  },

  payBillet: async (body: bank.WithTransactionAuth<models.PaymentBillet>) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL_V2}/pay-billet`,
      body
    );

    return data;
  },

  generateBillet: async (body: models.BilletInfoRequest) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL}/billet-cashin`,
      {
        ...body,
      }
    );
    return data;
  },

  generateQRCode: async (body: models.QRCodeRequest) => {
    const instance = getInstance();
    const { data } = await instance.post<models.QRCodeDetails>(
      `${BASE_URL}/generate-pix-qrcode`,
      {
        ...body,
      }
    );
    return data;
  },

  pixTransfer: async (
    requestBody: bank.WithTransactionAuth<models.PixTransferRequest>
  ) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL_V2}/pix-transfer`,
      requestBody
    );

    return data;
  },

  pixAgencyTransfer: async (
    requestBody: bank.WithTransactionAuth<models.ContactTransfer>
  ) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL}/pix-agency-account-transfer`,
      requestBody
    );

    return data;
  },

  pixAgencyScheduleTransfer: async (
    requestBody: bank.WithTransactionAuth<models.PixAgencyScheduled>
  ) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL}/transfer-schedule-without-key`,
      requestBody
    );

    return data;
  },

  pixWithdrawal: async (
    requestBody: bank.WithTransactionAuth<models.PixTransferRequest>
  ) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL}/pix-withdrawal`,
      requestBody
    );

    return data;
  },

  pixChange: async (
    requestBody: bank.WithTransactionAuth<models.PixTransferRequest>
  ) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL}/pix-change`,
      requestBody
    );

    return data;
  },

  getScheduledTransactions: async () => {
    const instance = getInstance();
    const { data } = await instance.get<GetTransactionsResponse>(
      `${BASE_URL}/schedule-transfers?type=${TransactionFlow.SCHEDULED}`
    );

    if (data.rows && Array.isArray(data.rows)) {
      data.rows.sort((a, b) => {
        const bankIdA = Number(String(a.bankId).slice(1)) || 0;
        const bankIdB = Number(String(b.bankId).slice(1)) || 0;
        return bankIdB - bankIdA;
      });
    }

    return data;
  },

  pixScheduledTransfer: async (
    requestBody: bank.WithTransactionAuth<models.PixTransferRequest>
  ) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL}/transfer-schedule`,
      requestBody
    );

    return data;
  },

  cancelPixScheduledTransfer: async (
    id: string,
    requestBody: { authenticate: bank.TransactionAuth }
  ) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL}/transfer-schedule/${id}/cancel`,
      requestBody
    );

    return data;
  },

  pixReversalTransfer: async (
    id: string,
    pin: string,
    amount: number,
    returnReasonCode: string,
    description?: string
  ) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL_V2}/${id}/return-pix-transfer`,
      {
        authenticate: {
          pin,
        },
        returnReasonCode,
        transaction: {
          amount,
          pixInfo: {
            description: description ?? '',
          },
        },
      }
    );

    return data;
  },

  contactTransfer: async (
    requestBody: bank.WithTransactionAuth<models.ContactTransfer>
  ) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL_V2}/favored-transfer`,
      requestBody
    );

    return data;
  },

  exportTransactionVoucher: async (transactionId: string) => {
    const instance = getInstance();
    const { data } = await instance.get<bank.Transaction>(
      `${BASE_URL}/${transactionId}/export-transaction-voucher`
    );

    return data;
  },

  sendEmail: async (billetUrl: string) => {
    const instance = getInstance();
    await instance.post(`${BASE_URL}/email-billet`, {
      billetUrl,
    });
  },

  getAccountPaymentDetail: async (
    paymentAccountId: string,
    params?: advancedFilterModels.PaymentDetailFilter
  ) => {
    const instance = getInstance();
    const { data } = await instance.get(
      `${BASE_URL_V2}/payment-account/${paymentAccountId}`,
      { params }
    );

    return data;
  },

  downloadReceipts: async (ids: string) => {
    const instance = getInstance();
    const { data } = await instance.post(
      `${BASE_URL_V2}/export-transactions-voucher`,
      {
        transactionsIdList: ids,
      }
    );

    return data;
  },

  p2pScheduledTransfer: async (
    requestBody: bank.WithTransactionAuth<models.P2pTransferRequest>
  ) => {
    const instance = getInstance();
    const { data } = await instance.post<bank.Transaction>(
      `${BASE_URL}/p2p-transfer-schedule`,
      requestBody
    );

    return data;
  },
};

export default TransactionApi;
